/**
 * Formula Expression parser (DYN-4.1).
 *
 * A mini domain-specific language used inside the Block Properties Table
 * (BTABLE) formula column, matching AutoCAD's expression syntax for
 * `Arith-Expression:` entries. Formulas reference the runtime values of
 * block parameters and other computed BTABLE entries to produce derived
 * property values shown in the Properties Palette.
 *
 * This module exposes the AST types, the tokeniser, the recursive-
 * descent parser, and dependency analysis. The evaluator and batch-
 * evaluation pipeline live in `formulaEvaluator.ts`.
 *
 * ─── Grammar (recursive-descent) ────────────────────────────────────
 *
 *   expr       → ternary
 *   ternary    → orExpr ('?' orExpr ':' ternary)?
 *   orExpr     → andExpr ('||' andExpr)*
 *   andExpr    → cmpExpr ('&&' cmpExpr)*
 *   cmpExpr    → sumExpr (('==' | '!=' | '<' | '>' | '<=' | '>=') sumExpr)?
 *   sumExpr    → mulExpr (('+' | '-') mulExpr)*
 *   mulExpr    → powExpr (('*' | '/' | '%') powExpr)*
 *   powExpr    → unary ('**' powExpr)?           // right-associative
 *   unary      → ('-' | '+' | '!') unary | primary
 *   primary    → number
 *              | string
 *              | boolean
 *              | identifier ('.' identifier)* ('(' args? ')')?
 *              | '(' expr ')'
 *   args       → expr (',' expr)*
 *
 * ─── Supported values ───────────────────────────────────────────────
 *
 *   - Numeric literals: 10, 3.14, -5, 1e6, .5
 *   - String literals:  "text", 'text'      (double or single quotes,
 *                                            backslash-escapes \\, \n, \t, \", \')
 *   - Boolean literals: true, false
 *   - Variable refs:    identifier          (resolved from scope)
 *   - Function calls:   Math.sin(x), Math.cos(x), Math.sqrt(x), Math.abs(x),
 *                       Math.min(a, b, …), Math.max(a, b, …),
 *                       Math.floor/ceil/round/trunc/sign(x),
 *                       Math.pow(a, b), Math.atan2(y, x),
 *                       Math.log/log2/log10/exp(x),
 *                       Math.hypot(…),
 *                       Math.PI / Math.E / Math.LN2 / Math.LN10 / Math.LOG2E /
 *                       Math.LOG10E / Math.SQRT2 (constants)
 *   - Arithmetic:       + - * / % **
 *   - Unary:            -x, +x, !x
 *   - String concat:    "W" + Width + "mm"  (any operand that is a string
 *                                             triggers string-concat)
 *   - Ternary:          cond ? then : else
 *   - Boolean ops:      && ||                  (short-circuit)
 *   - Comparisons:      == != < > <= >=
 *   - Parentheses:      (a + b) * c
 *
 * ─── Safety ─────────────────────────────────────────────────────────
 *
 * No `eval`, no `Function()` constructor, no `with` block, no prototype
 * access. Identifiers resolve ONLY through the provided `scope` map and
 * the whitelisted `Math.*` functions/constants. Any unknown identifier
 * or function name produces a descriptive error. Numeric operations go
 * through Number-typed JS arithmetic — no BigInt, no host globals.
 *
 * ─── Errors ─────────────────────────────────────────────────────────
 *
 * `parseFormula` throws `FormulaParseError` on malformed input with a
 * message pointing at the offending token / position. The evaluator
 * counterpart throws `FormulaEvalError` on runtime issues.
 */

// ═══════════════════════════════════════════════════════════════════
// AST types + errors — re-exported from formulaParser.types.ts so
// external consumers can keep importing them from this module.
// ═══════════════════════════════════════════════════════════════════

export {
  FormulaParseError,
  type BinaryOp,
  type BinaryOpNode,
  type BooleanLiteralNode,
  type FormulaAST,
  type FormulaValue,
  type FunctionCallNode,
  type NumberLiteralNode,
  type StringLiteralNode,
  type TernaryNode,
  type UnaryOpNode,
  type VariableNode,
} from "./formulaParser.types";

import { FormulaParseError, type FormulaAST } from "./formulaParser.types";

// ═══════════════════════════════════════════════════════════════════
// Tokeniser
// ═══════════════════════════════════════════════════════════════════

type TokenKind =
  | "number"
  | "string"
  | "boolean"
  | "identifier"
  | "op"
  | "punct"
  | "eof";

interface Token {
  readonly kind: TokenKind;
  readonly value: string;
  readonly position: number;
  readonly numberValue?: number;
  readonly booleanValue?: boolean;
}

const PUNCTUATION = new Set(["(", ")", ",", ".", "?", ":"]);

// Order matters — longer first so "**" wins before "*".
const OPERATORS: readonly string[] = [
  "**",
  "==",
  "!=",
  "<=",
  ">=",
  "&&",
  "||",
  "+",
  "-",
  "*",
  "/",
  "%",
  "<",
  ">",
  "!",
];

function isDigit(ch: string): boolean {
  return ch >= "0" && ch <= "9";
}

function isIdentStart(ch: string): boolean {
  return (
    (ch >= "a" && ch <= "z") ||
    (ch >= "A" && ch <= "Z") ||
    ch === "_" ||
    ch === "$"
  );
}

function isIdentCont(ch: string): boolean {
  return isIdentStart(ch) || isDigit(ch);
}

function tokenise(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = input.length;

  while (i < n) {
    const ch = input[i]!;

    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      i += 1;
      continue;
    }

    // Number literal.
    if (isDigit(ch) || (ch === "." && i + 1 < n && isDigit(input[i + 1]!))) {
      const start = i;
      let sawDot = false;
      let sawExp = false;
      while (i < n) {
        const c = input[i]!;
        if (isDigit(c)) {
          i += 1;
          continue;
        }
        if (c === "." && !sawDot && !sawExp) {
          sawDot = true;
          i += 1;
          continue;
        }
        if ((c === "e" || c === "E") && !sawExp) {
          sawExp = true;
          i += 1;
          if (i < n && (input[i] === "+" || input[i] === "-")) {
            i += 1;
          }
          continue;
        }
        break;
      }
      const literal = input.slice(start, i);
      const num = Number(literal);
      if (!Number.isFinite(num)) {
        throw new FormulaParseError(
          `Invalid number literal "${literal}"`,
          start,
        );
      }
      tokens.push({
        kind: "number",
        value: literal,
        position: start,
        numberValue: num,
      });
      continue;
    }

    // String literal.
    if (ch === '"' || ch === "'") {
      const quote = ch;
      const start = i;
      i += 1;
      let value = "";
      while (i < n && input[i] !== quote) {
        const c = input[i]!;
        if (c === "\\" && i + 1 < n) {
          const esc = input[i + 1]!;
          switch (esc) {
            case "n":
              value += "\n";
              break;
            case "t":
              value += "\t";
              break;
            case "r":
              value += "\r";
              break;
            case "\\":
              value += "\\";
              break;
            case '"':
              value += '"';
              break;
            case "'":
              value += "'";
              break;
            default:
              value += esc;
              break;
          }
          i += 2;
          continue;
        }
        value += c;
        i += 1;
      }
      if (i >= n) {
        throw new FormulaParseError("Unterminated string literal", start);
      }
      i += 1;
      tokens.push({ kind: "string", value, position: start });
      continue;
    }

    // Identifier / boolean keyword.
    if (isIdentStart(ch)) {
      const start = i;
      while (i < n && isIdentCont(input[i]!)) {
        i += 1;
      }
      const name = input.slice(start, i);
      if (name === "true" || name === "false") {
        tokens.push({
          kind: "boolean",
          value: name,
          position: start,
          booleanValue: name === "true",
        });
      } else {
        tokens.push({ kind: "identifier", value: name, position: start });
      }
      continue;
    }

    // Operator (multi-char first).
    let matchedOp: string | null = null;
    for (const op of OPERATORS) {
      if (input.startsWith(op, i)) {
        matchedOp = op;
        break;
      }
    }
    if (matchedOp !== null) {
      tokens.push({ kind: "op", value: matchedOp, position: i });
      i += matchedOp.length;
      continue;
    }

    if (PUNCTUATION.has(ch)) {
      tokens.push({ kind: "punct", value: ch, position: i });
      i += 1;
      continue;
    }

    throw new FormulaParseError(`Unexpected character "${ch}"`, i);
  }

  tokens.push({ kind: "eof", value: "", position: i });
  return tokens;
}

// ═══════════════════════════════════════════════════════════════════
// Parser (recursive-descent, precedence climbing by grammar level)
// ═══════════════════════════════════════════════════════════════════

class FormulaParser {
  private tokens: readonly Token[];
  private pos = 0;

  constructor(tokens: readonly Token[]) {
    this.tokens = tokens;
  }

  private peek(): Token {
    return this.tokens[this.pos]!;
  }

  private advance(): Token {
    const t = this.tokens[this.pos]!;
    this.pos += 1;
    return t;
  }

  private matchOp(...ops: string[]): boolean {
    const t = this.peek();
    return t.kind === "op" && ops.includes(t.value);
  }

  private matchPunct(...chars: string[]): boolean {
    const t = this.peek();
    return t.kind === "punct" && chars.includes(t.value);
  }

  private expectPunct(ch: string): void {
    const t = this.peek();
    if (t.kind !== "punct" || t.value !== ch) {
      throw new FormulaParseError(
        `Expected '${ch}', got '${t.value || t.kind}'`,
        t.position,
      );
    }
    this.advance();
  }

  parse(): FormulaAST {
    const expr = this.parseTernary();
    const t = this.peek();
    if (t.kind !== "eof") {
      throw new FormulaParseError(
        `Unexpected token '${t.value || t.kind}'`,
        t.position,
      );
    }
    return expr;
  }

  private parseTernary(): FormulaAST {
    const cond = this.parseOrExpr();
    if (this.matchPunct("?")) {
      this.advance();
      const thenBranch = this.parseOrExpr();
      this.expectPunct(":");
      const elseBranch = this.parseTernary();
      return {
        kind: "ternary",
        condition: cond,
        then: thenBranch,
        else: elseBranch,
      };
    }
    return cond;
  }

  private parseOrExpr(): FormulaAST {
    let left = this.parseAndExpr();
    while (this.matchOp("||")) {
      this.advance();
      const right = this.parseAndExpr();
      left = { kind: "binary", op: "||", left, right };
    }
    return left;
  }

  private parseAndExpr(): FormulaAST {
    let left = this.parseCmpExpr();
    while (this.matchOp("&&")) {
      this.advance();
      const right = this.parseCmpExpr();
      left = { kind: "binary", op: "&&", left, right };
    }
    return left;
  }

  private parseCmpExpr(): FormulaAST {
    const left = this.parseSumExpr();
    if (this.matchOp("==", "!=", "<", ">", "<=", ">=")) {
      const op = this.advance().value as "==" | "!=" | "<" | ">" | "<=" | ">=";
      const right = this.parseSumExpr();
      return { kind: "binary", op, left, right };
    }
    return left;
  }

  private parseSumExpr(): FormulaAST {
    let left = this.parseMulExpr();
    while (this.matchOp("+", "-")) {
      const op = this.advance().value as "+" | "-";
      const right = this.parseMulExpr();
      left = { kind: "binary", op, left, right };
    }
    return left;
  }

  private parseMulExpr(): FormulaAST {
    let left = this.parsePowExpr();
    while (this.matchOp("*", "/", "%")) {
      const op = this.advance().value as "*" | "/" | "%";
      const right = this.parsePowExpr();
      left = { kind: "binary", op, left, right };
    }
    return left;
  }

  private parsePowExpr(): FormulaAST {
    const left = this.parseUnary();
    if (this.matchOp("**")) {
      this.advance();
      const right = this.parsePowExpr();
      return { kind: "binary", op: "**", left, right };
    }
    return left;
  }

  private parseUnary(): FormulaAST {
    if (this.matchOp("-", "+", "!")) {
      const op = this.advance().value as "-" | "+" | "!";
      const operand = this.parseUnary();
      return { kind: "unary", op, operand };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): FormulaAST {
    const t = this.peek();
    if (t.kind === "number") {
      this.advance();
      return { kind: "number", value: t.numberValue! };
    }
    if (t.kind === "string") {
      this.advance();
      return { kind: "string", value: t.value };
    }
    if (t.kind === "boolean") {
      this.advance();
      return { kind: "boolean", value: t.booleanValue! };
    }
    if (t.kind === "identifier") {
      this.advance();
      const path: string[] = [t.value];
      while (this.matchPunct(".")) {
        this.advance();
        const next = this.peek();
        if (next.kind !== "identifier") {
          throw new FormulaParseError(
            `Expected identifier after '.', got '${next.value || next.kind}'`,
            next.position,
          );
        }
        this.advance();
        path.push(next.value);
      }
      if (this.matchPunct("(")) {
        this.advance();
        const args: FormulaAST[] = [];
        if (!this.matchPunct(")")) {
          args.push(this.parseTernary());
          while (this.matchPunct(",")) {
            this.advance();
            args.push(this.parseTernary());
          }
        }
        this.expectPunct(")");
        return { kind: "call", name: path, args };
      }
      return { kind: "variable", path };
    }
    if (this.matchPunct("(")) {
      this.advance();
      const expr = this.parseTernary();
      this.expectPunct(")");
      return expr;
    }
    throw new FormulaParseError(
      `Unexpected token '${t.value || t.kind}'`,
      t.position,
    );
  }
}

/**
 * Parse a formula expression source string into an AST. Throws
 * `FormulaParseError` on malformed input.
 */
export function parseFormula(expression: string): FormulaAST {
  const tokens = tokenise(expression);
  const parser = new FormulaParser(tokens);
  return parser.parse();
}

// ═══════════════════════════════════════════════════════════════════
// Dependency analysis — what scope identifiers does the AST reference?
// ═══════════════════════════════════════════════════════════════════

/**
 * Return the set of top-level scope identifiers referenced by the AST.
 * `Math.*` references are filtered out — they resolve to whitelisted
 * host constants/functions, not scope values. Dotted scope access (e.g.
 * `foo.x`) contributes the joined key "foo.x" as a dependency so the
 * caller can match it against scope keys exactly.
 */
export function collectDependencies(ast: FormulaAST): Set<string> {
  const out = new Set<string>();
  walk(ast, out);
  return out;
}

function walk(ast: FormulaAST, out: Set<string>): void {
  switch (ast.kind) {
    case "number":
    case "string":
    case "boolean":
      return;
    case "variable":
      if (ast.path[0] !== "Math") {
        out.add(ast.path.join("."));
      }
      return;
    case "unary":
      walk(ast.operand, out);
      return;
    case "binary":
      walk(ast.left, out);
      walk(ast.right, out);
      return;
    case "ternary":
      walk(ast.condition, out);
      walk(ast.then, out);
      walk(ast.else, out);
      return;
    case "call":
      if (ast.name[0] !== "Math") {
        // Custom functions aren't supported — record as dependency so
        // evaluator can surface a clear error if scope somehow provides it.
        out.add(ast.name.join("."));
      }
      for (const a of ast.args) walk(a, out);
      return;
  }
}
